import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { hrAPI } from '../../services/api';
import { blobErrorText } from './hrShared';
import './HRApp.css';

// V70.159 (P0) — "Bordrolarım": HR tarafında bordro çalıştırılıp yayınlanabiliyor ama
// çalışan tarafı "Yakında" stub'ıydı (spec §9.2: release blocker). Bu ekran YALNIZ
// yayınlanmış dönemleri listeler; PDF, oturumdaki kullanıcının kendi kaydından üretilir.

const fmt = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const donemLabel = (donem) => {
  const m = /^(\d{4})-(\d{2})$/.exec(donem || '');
  return m ? `${AY[parseInt(m[2], 10) - 1]} ${m[1]}` : (donem || '—');
};

export default function HRPayslips() {
  const [rows, setRows] = useState([]);
  const [linked, setLinked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrAPI.selfPayslips();
      setRows(r.data?.data || []);
      setLinked(r.data?.linked !== false);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (runId, donem) => {
    setDownloading(runId);
    try {
      const r = await hrAPI.selfPayslipPdf(runId);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bordro_${donem}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Bordro indirilemedi: ' + await blobErrorText(e)); // V71 — blob hata gövdesi okunur
    } finally { setDownloading(null); }
  };

  if (loading) return <div className="loading-state">Yükleniyor…</div>;

  if (error) {
    return (
      <div className="hrps-state hrps-state-err">
        <div className="hrps-state-ic">⚠️</div>
        <h3>Bordrolar yüklenemedi</h3>
        <p>{error}</p>
        <button className="hrps-btn" onClick={load}>Tekrar dene</button>
      </div>
    );
  }

  if (!linked) {
    return (
      <div className="hrps-state">
        <div className="hrps-state-ic">🔗</div>
        <h3>İK kaydınız bağlı değil</h3>
        <p>Kullanıcı hesabınız bir personel kaydına bağlanmadığı için bordrolarınız gösterilemiyor. İK ile iletişime geçin.</p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="hrps-state">
        <div className="hrps-state-ic">📄</div>
        <h3>Henüz yayınlanmış bordronuz yok</h3>
        <p>Bir bordro dönemi İK tarafından onaylanıp yayınlandığında burada görünür ve PDF olarak indirebilirsiniz.</p>
      </div>
    );
  }

  return (
    <div className="hrps-wrap">
      <div className="hrps-head">
        {/* V71 — gruplu görünümde başlık şeritte; dönem sayısı görünür kalır */}
        <h2 className="pg-hide-grouped">Bordrolarım</h2>
        <span className="hrps-count">{rows.length} yayınlanmış dönem</span>
      </div>

      <div className="hrps-list">
        {rows.map(r => {
          const open = openId === r.run_id;
          const kesintiler = [
            ['SGK İşçi', r.sgk_isci],
            ['İşsizlik', r.isvr_isci],
            ['Gelir Vergisi', r.gv],
            ['Damga Vergisi', r.dv],
            ['Diğer Kesintiler', r.kesinti_tutar],
          ].filter(([, v]) => Number(v || 0) !== 0);
          const kazanclar = [
            ['Brüt Ücret', r.brut],
            ['Fazla Mesai', r.mesai_tutar],
            ['Prim', r.prim_tutar],
          ].filter(([, v]) => Number(v || 0) !== 0);

          return (
            <div key={r.run_id} className={`hrps-card ${open ? 'open' : ''}`}>
              <button className="hrps-card-head" onClick={() => setOpenId(open ? null : r.run_id)}>
                <div className="hrps-donem">
                  <span className="hrps-donem-ad">{donemLabel(r.donem)}</span>
                  {r.published_at && (
                    <span className="hrps-donem-sub">Yayın: {String(r.published_at).replace('T', ' ').slice(0, 10)}</span>
                  )}
                </div>
                <div className="hrps-net">
                  <span className="hrps-net-lbl">Net ödenen</span>
                  <span className="hrps-net-val">{fmt(r.net)} ₺</span>
                </div>
                <span className="hrps-chev">{open ? '▲' : '▼'}</span>
              </button>

              {open && (
                <div className="hrps-detail">
                  <div className="hrps-cols">
                    <div>
                      <h4>Kazançlar</h4>
                      {kazanclar.map(([k, v]) => (
                        <div className="hrps-row" key={k}><span>{k}</span><span>{fmt(v)} ₺</span></div>
                      ))}
                    </div>
                    <div>
                      <h4>Kesintiler</h4>
                      {kesintiler.length ? kesintiler.map(([k, v]) => (
                        <div className="hrps-row" key={k}><span>{k}</span><span className="neg">-{fmt(v)} ₺</span></div>
                      )) : <div className="hrps-row muted"><span>Kesinti yok</span><span>—</span></div>}
                    </div>
                  </div>
                  <div className="hrps-row hrps-row-net"><span>NET ÖDENEN</span><span>{fmt(r.net)} ₺</span></div>
                  <button className="hrps-btn" disabled={downloading === r.run_id}
                    onClick={() => handleDownload(r.run_id, r.donem)}>
                    {downloading === r.run_id ? 'Hazırlanıyor...' : '📄 PDF indir'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
